import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import CTABand from "@/components/CTABand";

const stats = [
  { value: "$13.1B by 2035", label: "AI governance market, 31% CAGR" },
  { value: "80+", label: "Jurisdictions advancing binding AI rules" },
  { value: "~92%", label: "Of enterprises lack a comprehensive AI governance framework" },
];

const capabilities = [
  { title: "Deployment readiness evaluation", line: "Independent assessment of whether an AI system does what it claims before it goes live." },
  { title: "Reliability drift measurement", line: "Ongoing measurement of how model behavior changes over time, so failures are caught early." },
  { title: "Agent governance and containment", line: "Standards and controls for autonomous systems that act on their own." },
  { title: "Governance integration", line: "Evaluation evidence built into enterprise risk, audit, and compliance systems." },
];

const segments = [
  { title: "Regulated enterprise", line: "Firms deploying AI inside regulated processes." },
  { title: "Standards bodies and regulators", line: "Institutions that need independent evaluation infrastructure." },
  { title: "Government and defense", line: "Agencies and contractors deploying AI in high-stakes contexts." },
  { title: "AI developers and deployers", line: "Builders who need third-party evidence for their claims." },
];

export default function Home() {
  useSeo(
    "Reform Intelligence® | Independent AI Evaluation and Governance Infrastructure",
    "Reform Intelligence builds the independent evaluation and governance infrastructure the world needs to trust artificial intelligence."
  );

  return (
    <>
      <section data-testid="home-hero" className="border-b border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-24 sm:px-8 sm:py-32">
          <Eyebrow>Independent AI evaluation and governance infrastructure</Eyebrow>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-5 max-w-3xl font-serif2 text-4xl font-semibold leading-tight tracking-tight text-[#1F2D4E] sm:text-5xl lg:text-6xl"
          >
            The institution that makes AI accountable.
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
            className="mt-6 max-w-2xl text-lg leading-relaxed text-[#5A6B8C]"
          >
            Reform Intelligence® builds the independent evaluation and governance infrastructure the world needs to trust artificial intelligence.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.24, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-request-briefing"
              className="bg-[#1F2D4E] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-white transition-colors hover:bg-[#B08D3F]"
            >
              Request a briefing
            </Link>
            <Link
              to="/what-we-do"
              data-testid="hero-see-approach"
              className="border border-[#1F2D4E] px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.12em] text-[#1F2D4E] transition-colors hover:border-[#B08D3F] hover:text-[#B08D3F]"
            >
              See our approach
            </Link>
          </motion.div>
        </div>
      </section>

      <section data-testid="stat-band" className="border-b border-[#E4E7EC] bg-white">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-10 px-5 py-14 sm:grid-cols-3 sm:px-8">
          {stats.map((s, i) => (
            <Reveal key={s.value} delay={i * 0.1}>
              <div data-testid={`stat-${i}`} className="border-l-2 border-[#B08D3F] pl-5">
                <p className="font-serif2 text-3xl font-semibold text-[#1F2D4E] sm:text-4xl">{s.value}</p>
                <p className="mt-2 text-sm leading-relaxed text-[#5A6B8C]">{s.label}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <h2 className="max-w-2xl font-serif2 text-3xl font-semibold leading-snug tracking-tight text-[#1F2D4E] sm:text-4xl">
              The capability is racing ahead of the accountability.
            </h2>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-[#1A1A1A]">
              Artificial intelligence is being wired into the decisions that run economies, governments, and daily life. The capability is advancing faster than the infrastructure to evaluate whether it is reliable, safe, and accountable. That infrastructure barely exists. Reform Intelligence is building it.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="home-what-we-do" className="border-y border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <Eyebrow>What we do</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif2 text-3xl font-semibold leading-snug tracking-tight text-[#1F2D4E] sm:text-4xl">
              Four instruments of accountability.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-px border border-[#E4E7EC] bg-[#E4E7EC] sm:grid-cols-2">
            {capabilities.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.08} className="bg-white">
                <div data-testid={`capability-card-${i}`} className="h-full p-8 transition-colors hover:bg-[#F5F6F8]">
                  <h3 className="font-serif2 text-xl font-semibold text-[#1F2D4E]">{c.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-[#5A6B8C]">{c.line}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <Link to="/what-we-do" data-testid="see-how-we-work" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#B08D3F] transition-colors hover:text-[#1F2D4E]">
              See how we work <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1fr_1.4fr] lg:gap-20">
            <Reveal>
              <img src="/assets/emblem-navy.png" alt="Reform Intelligence column emblem" className="w-40 sm:w-52" />
            </Reveal>
            <Reveal delay={0.1}>
              <Eyebrow>Independence</Eyebrow>
              <h2 className="mt-4 font-serif2 text-3xl font-semibold leading-snug tracking-tight text-[#1F2D4E] sm:text-4xl">
                Independence is the moat.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#1A1A1A]">
                The credibility of an evaluator is inseparable from its independence. A company that builds AI cannot be trusted to grade it. Reform Intelligence is independent by design: we hold no equity or ownership stake in any company we evaluate, our methodology is published so it can be reproduced and challenged, and our fee doesn't change based on the result. It is the same position that made auditors and rating agencies indispensable to the markets they serve.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      <section data-testid="home-who-we-serve" className="border-y border-[#E4E7EC] bg-[#F5F6F8]">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <Eyebrow>Who we serve</Eyebrow>
            <h2 className="mt-4 max-w-2xl font-serif2 text-3xl font-semibold leading-snug tracking-tight text-[#1F2D4E] sm:text-4xl">
              Institutions that cannot afford to be wrong.
            </h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {segments.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.08}>
                <Link
                  to="/who-we-serve"
                  data-testid={`segment-tile-${i}`}
                  className="block h-full border border-[#E4E7EC] bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-[#B08D3F]"
                >
                  <h3 className="font-serif2 text-lg font-semibold text-[#1F2D4E]">{s.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#5A6B8C]">{s.line}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="insights-teaser" className="bg-white">
        <div className="mx-auto max-w-6xl px-5 py-20 sm:px-8 sm:py-28">
          <Reveal>
            <Eyebrow>Insights</Eyebrow>
            <h2 className="mt-4 font-serif2 text-3xl font-semibold tracking-tight text-[#1F2D4E] sm:text-4xl">From our research.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/insights/ai-governance-manifesto"
              data-testid="insights-teaser-card"
              className="mt-10 block max-w-2xl border border-[#E4E7EC] bg-[#F5F6F8] p-8 transition-all hover:-translate-y-0.5 hover:border-[#B08D3F]"
            >
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B08D3F]">Frameworks · New</p>
              <h3 className="mt-3 font-serif2 text-xl font-semibold text-[#1F2D4E]">The Reform AI™ Governance Manifesto</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#5A6B8C]">Our view on what trustworthy AI requires, and the institution being built to deliver it.</p>
              <p className="mt-3 text-xs text-[#98A0B3]">Dr. Ben E. Magee, Founder</p>
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/insights" data-testid="insights-teaser-link" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#B08D3F] transition-colors hover:text-[#1F2D4E]">
              Visit Insights <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABand line="Build AI the world can trust." />
    </>
  );
}
